"use client";

import Link from "next/link";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  FileSearch,
  Link2,
  ListChecks,
  MessageSquareText,
  SearchCheck,
  ShieldCheck,
  Wrench,
} from "lucide-react";

import { StructuredData } from "@/components/common";
import ServiceFaq from "@/components/services/ServiceFaq";
import SeoEnquiryForm from "@/components-pages/services/SeoEnquiryForm";
import {
  AIVC_FORM_ID,
  ASSISTANTS,
  FAQS,
  SAMPLE_REPORT,
  SINGAPORE_AI_SEO_PATH,
  STEPS,
  WHY_IT_MATTERS,
  YOUR_BUSINESS,
  sampleReportScore,
  type SampleAnswer,
} from "@/content/ai-visibility-check";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { renderAccent } from "@/lib/render-accent";
import { whatsappHref } from "@/lib/whatsapp";

const STEP_ICONS = [MessageSquareText, Bot, ListChecks];

function AnswerCell({
  answer,
  locale,
}: {
  answer: SampleAnswer;
  locale: Locale;
}) {
  if (answer === null) {
    return (
      <span className="text-xs italic text-neutral-muted">
        {t(locale, "No AI Overview shown")}
      </span>
    );
  }
  if (answer.named.length === 0) {
    return (
      <span className="text-xs italic text-neutral-muted">
        {t(locale, "No business named")}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap gap-1.5">
      {answer.named.map((name) =>
        name === YOUR_BUSINESS ? (
          <span
            key={name}
            className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success"
          >
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            {t(locale, name)}
          </span>
        ) : (
          <span
            key={name}
            className="inline-flex rounded-full border border-border px-2.5 py-0.5 text-xs text-neutral-emphasis"
          >
            {t(locale, name)}
          </span>
        ),
      )}
    </span>
  );
}

function SampleReport({ locale }: { locale: Locale }) {
  const score = sampleReportScore();
  const scoreLine = t(locale, "{business} was named in {named} of {shown} answers.")
    .replace("{business}", t(locale, YOUR_BUSINESS))
    .replace("{named}", String(score.named))
    .replace("{shown}", String(score.shown));

  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl shadow-neutral-emphasis/5">
      {/* The example label sits in plain sight, above everything else in the report. */}
      <div className="flex flex-col gap-2 border-b border-warning/30 bg-warning/10 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 sm:px-8">
        <span className="inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded-full bg-warning px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em] text-neutral-emphasis">
          {t(locale, SAMPLE_REPORT.label)}
        </span>
        <p className="text-sm text-neutral-emphasis">
          {t(locale, SAMPLE_REPORT.disclaimer)}
        </p>
      </div>

      <div className="px-5 py-7 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-muted">
          {t(locale, "Sample report")}
        </p>
        <h3 className="mt-2 font-display text-2xl font-bold text-neutral-emphasis">
          {t(locale, SAMPLE_REPORT.heading)}
        </h3>

        {/* 1. The five questions, by assistant */}
        <h4 className="mt-8 flex items-center gap-2 text-sm font-semibold text-neutral-emphasis">
          <SearchCheck className="h-4 w-4 text-primary" aria-hidden />
          {t(locale, "1. Five customer questions, and who each AI named")}
        </h4>
        <div className="mt-4 divide-y divide-border rounded-2xl border border-border">
          <div className="hidden bg-neutral-subtle/40 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-muted md:grid md:grid-cols-[1.5fr_repeat(3,1fr)] md:gap-4">
            <span>{t(locale, "Question")}</span>
            {ASSISTANTS.map((assistant) => (
              <span key={assistant}>{assistant}</span>
            ))}
          </div>
          {SAMPLE_REPORT.questions.map((q, index) => (
            <div
              key={q.question}
              className="grid gap-3 px-4 py-4 md:grid-cols-[1.5fr_repeat(3,1fr)] md:items-start md:gap-4"
            >
              <p className="text-sm font-medium text-neutral-emphasis">
                <span className="mr-2 font-mono text-xs text-neutral-muted">
                  Q{index + 1}
                </span>
                {t(locale, q.question)}
              </p>
              {ASSISTANTS.map((assistant) => (
                <div key={assistant} className="flex items-start gap-3 md:block">
                  <span className="w-36 shrink-0 text-xs font-semibold text-neutral-muted md:hidden">
                    {assistant}
                  </span>
                  <AnswerCell answer={q.answers[assistant]} locale={locale} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="mt-4 rounded-xl bg-neutral-subtle/40 px-4 py-3 text-sm font-semibold text-neutral-emphasis">
          {scoreLine}
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* 2. Sources cited */}
          <div className="rounded-2xl border border-border p-5">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-emphasis">
              <Link2 className="h-4 w-4 text-primary" aria-hidden />
              {t(locale, "2. Sources the answers cited")}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {SAMPLE_REPORT.sources.map((item) => (
                <li
                  key={item.source}
                  className="flex items-start justify-between gap-4 text-sm text-neutral-muted"
                >
                  <span>{t(locale, item.source)}</span>
                  <span className="shrink-0 font-mono text-xs text-neutral-emphasis">
                    {t(locale, "{n}x").replace("{n}", String(item.cited))}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* 3. What is holding the website back */}
          <div className="rounded-2xl border border-border p-5">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-neutral-emphasis">
              <Wrench className="h-4 w-4 text-primary" aria-hidden />
              {t(locale, "3. What is holding the website back")}
            </h4>
            <ol className="mt-4 space-y-3">
              {SAMPLE_REPORT.fixes.map((fix, index) => (
                <li key={fix.title} className="flex gap-3 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <span>
                    <span className="font-semibold text-neutral-emphasis">
                      {t(locale, fix.title)}.
                    </span>{" "}
                    <span className="text-neutral-muted">{t(locale, fix.body)}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* 4. Recommended plan */}
        <p className="mt-6 flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm text-neutral-emphasis">
          <ListChecks className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>
            <span className="font-semibold">{t(locale, "4. Recommended plan.")}</span>{" "}
            {t(locale, SAMPLE_REPORT.recommendation)}
          </span>
        </p>
      </div>
    </div>
  );
}

export default function AiVisibilityCheck({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const L = (path: string) => getLocalizedPathname(path, locale);
  const formHref = `#${AIVC_FORM_ID}`;

  return (
    <main className="bg-background">
      <StructuredData
        type="faq"
        faqs={FAQS.map((f) => ({
          question: t(locale, f.question),
          answer: t(locale, f.answer),
        }))}
      />

      {/* Hero */}
      <section className="border-b border-border py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div>
            <nav aria-label={t(locale, "Breadcrumb")} className="mb-6 text-sm text-neutral-muted">
              <Link href={L("/")} className="transition-colors hover:text-primary">
                {t(locale, "Home")}
              </Link>
              <span className="mx-2" aria-hidden>
                /
              </span>
              <span className="text-neutral-emphasis">
                {t(locale, "Free AI visibility check")}
              </span>
            </nav>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
              {t(locale, "Free · No obligation")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-neutral-emphasis sm:text-5xl">
              {renderAccent(
                t(locale, "Free AI visibility check: is your business on [[ChatGPT]]?"),
              )}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Your customers now ask ChatGPT, Gemini and Google who to call. Send us your website. We ask those AI assistants five questions your customers would ask, then show you who gets named, which sources are cited, and what is holding your website back.",
              )}
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href={formHref}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Get my free AI visibility check")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <a
                href="#sample-report"
                className="inline-flex items-center justify-center rounded-full border border-border px-7 py-3.5 font-semibold text-neutral-emphasis transition hover:border-primary/50"
              >
                {t(locale, "See a sample report")}
              </a>
            </div>
            <p className="mt-6 text-sm text-neutral-muted">
              <Link
                href={L(SINGAPORE_AI_SEO_PATH)}
                className="font-semibold text-primary underline-offset-4 hover:underline"
              >
                {t(locale, "In Singapore? See AI SEO Singapore")}
              </Link>
            </p>
          </div>

          {/* At-a-glance card: what the check covers */}
          <div className="rounded-3xl border border-border bg-card p-7 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-muted">
              {t(locale, "What we check")}
            </p>
            <ul className="mt-5 space-y-4">
              {[
                { icon: MessageSquareText, text: "Five questions your customers would ask" },
                { icon: Bot, text: "Asked to ChatGPT, Gemini and Google AI Overviews" },
                { icon: SearchCheck, text: "Who gets named instead of you" },
                { icon: Link2, text: "Which sources the answers cite" },
                { icon: FileSearch, text: "What is holding your website back" },
                { icon: ListChecks, text: "A recommended plan, with no obligation" },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-sm text-neutral-emphasis">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" aria-hidden />
                  </span>
                  <span className="pt-1.5">{t(locale, text)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What you get: sample report */}
      <section id="sample-report" className="scroll-mt-20 bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
              {t(locale, "What you get")}
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-neutral-emphasis sm:text-4xl">
              {t(locale, "Who AI names, what it cites, and what to fix")}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Here is the format, shown with a made-up business. Your check uses your business, your market and questions your customers would ask.",
              )}
            </p>
          </div>
          <div className="mt-10">
            <SampleReport locale={locale} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
            {t(locale, "How it works")}
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-neutral-emphasis sm:text-4xl">
            {t(locale, "Three steps, and you only do the first")}
          </h2>
          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((step, index) => {
              const Icon = STEP_ICONS[index];
              return (
                <li key={step.title} className="rounded-3xl border border-border bg-card p-7">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" aria-hidden />
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-neutral-muted">
                      {t(locale, "Step {n}").replace("{n}", String(index + 1))}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-neutral-emphasis">
                    {t(locale, step.title)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, step.body)}
                  </p>
                </li>
              );
            })}
          </ol>
          <p className="mt-8 text-sm text-neutral-muted">
            {t(locale, "Want to see what the plans include first?")}{" "}
            <Link
              href={L("/services/seo-services-malaysia/")}
              className="font-semibold text-primary underline underline-offset-2"
            >
              {t(locale, "Compare AI SEO plans")}
            </Link>
          </p>
        </div>
      </section>

      {/* Why it matters */}
      <section className="border-y border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
            {t(locale, "Why AI visibility matters")}
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-neutral-emphasis sm:text-4xl">
            {t(locale, "Your customers already ask AI, in Malaysia and Singapore")}
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {WHY_IT_MATTERS.map((item) => (
              <div key={item.sourceUrl} className="flex flex-col rounded-3xl border border-border bg-card p-7">
                <p className="font-display text-2xl font-bold text-neutral-emphasis">
                  {t(locale, item.stat)}
                </p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, item.body)}
                </p>
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 text-xs font-semibold text-primary underline underline-offset-2"
                >
                  {t(locale, "Source:")} {item.sourceLabel}
                </a>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-neutral-muted">
            {t(
              locale,
              "Nobody can guarantee that an AI answer will name your business. What the check gives you is a clear baseline, and a short list of what to fix.",
            )}{" "}
            <Link
              href="/blog/get-recommended-by-chatgpt-google-ai-overviews/"
              className="font-semibold text-primary underline underline-offset-2"
            >
              {t(locale, "Read how to get recommended by ChatGPT")}
            </Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr] lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
              {t(locale, "Frequently asked questions")}
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-neutral-emphasis">
              {t(locale, "About the free AI visibility check")}
            </h2>
          </div>
          <ServiceFaq items={FAQS} />
        </div>
      </section>

      {/* CTA + form */}
      <section
        id={AIVC_FORM_ID}
        className="scroll-mt-20 border-t border-border bg-neutral-subtle/35 py-16 lg:py-24"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr] lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">
              {t(locale, "Free AI visibility check")}
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-neutral-emphasis sm:text-4xl">
              {t(locale, "Find out who AI recommends instead of you")}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Tell us your website and what you sell. The form is set to the free check, so you do not need to pick a plan.",
              )}
            </p>
            <div className="mt-8 space-y-4 text-sm leading-relaxed text-neutral-muted">
              <p className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                {t(locale, "Free, with no obligation.")}
              </p>
              <p className="flex items-start gap-3">
                <SearchCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                {t(locale, "Real answers from ChatGPT, Gemini and Google AI Overviews.")}
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-3 text-sm">
              <a
                href={whatsappHref(
                  "Hi Anchor Sprint, I'd like a free AI visibility check for my website.",
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-track-event="contact_whatsapp_click"
                data-track-location="ai_visibility_check"
                data-track-phone="601159246128"
                className="inline-flex items-center gap-2 font-semibold text-primary underline-offset-4 hover:underline"
              >
                {t(locale, "Prefer WhatsApp? Message us your website")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <Link
                href={L(SINGAPORE_AI_SEO_PATH)}
                className="inline-flex items-center gap-2 font-semibold text-primary underline-offset-4 hover:underline"
              >
                {t(locale, "In Singapore? See AI SEO Singapore")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>
          <div className="rounded-3xl border border-border bg-background p-6 shadow-xl shadow-neutral-emphasis/5 sm:p-8 lg:p-10">
            <SeoEnquiryForm market="my" locale={locale} source="ai-visibility-check" />
          </div>
        </div>
      </section>
    </main>
  );
}
